import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { formatPhone, usePublicSettings } from '@/layouts/PublicShell';
import { Clause, PageHero, Prose } from './shared';

const LAST_UPDATED = '8 August 2026';

/**
 * Privacy policy.
 *
 * Written against what the software actually does — the fields it collects, how
 * identification numbers are stored, who can read them — rather than a
 * boilerplate template. The draft notice stays until the association has
 * reviewed it; publishing an unreviewed policy as though it were adopted would
 * be a misrepresentation.
 */
export default function Privacy() {
  const org = usePublicSettings();

  return (
    <>
      <PageHero
        eyebrow="Privacy policy"
        title="What we hold about you, and why."
        description={`How ${org['org.name']} collects, stores and uses personal information in the member lending platform.`}
      >
        <p className="numeric mt-6 text-sm text-foreground-secondary">
          Last updated {LAST_UPDATED} · Version 1.0
        </p>
      </PageHero>

      <Prose>
        <DraftNotice />

        <Clause number="1" title="Who is responsible for your information">
          <p>
            The <strong>Aliu Abdulkabir Foundation</strong> and the Obafemi Awolowo University
            Muslim Graduates&rsquo; Association (together &ldquo;AAF-UNIFEMGA&rdquo;,
            &ldquo;we&rdquo;) are jointly responsible for the personal information held in this
            platform. UNIFEMGA administers it day to day. You can reach us at{' '}
            <a href={`mailto:${org['org.email']}`} className="font-semibold text-primary hover:underline">
              {org['org.email']}
            </a>{' '}
            or{' '}
            <a href={`tel:${org['org.phone']}`} className="numeric font-semibold text-primary hover:underline">
              {formatPhone(org['org.phone'])}
            </a>
            , or at {org['org.address']}.
          </p>
        </Clause>

        <Clause number="2" title="What we collect">
          <p>We collect only what the lending scheme needs to operate:</p>
          <ul className="ml-5 list-disc space-y-1.5">
            <li>
              <strong className="text-foreground">Account details</strong> — your name, email
              address, telephone number and UNIFEMGA membership number.
            </li>
            <li>
              <strong className="text-foreground">Identity verification (KYC)</strong> — your full
              legal name, date of birth, gender, residential address, state and local government
              area, your identification type and number, and any documents you upload.
            </li>
            <li>
              <strong className="text-foreground">Employment and income</strong> — your employment
              status, employer, job title and income band, both in your profile and again on each
              loan application.
            </li>
            <li>
              <strong className="text-foreground">Next of kin</strong> — their name, relationship to
              you, telephone number, email address and address.
            </li>
            <li>
              <strong className="text-foreground">Bank accounts</strong> — the bank, account number
              and account name you want a loan paid into.
            </li>
            <li>
              <strong className="text-foreground">Loan records</strong> — your applications,
              approvals, repayment schedules and every repayment recorded against them.
            </li>
            <li>
              <strong className="text-foreground">Technical records</strong> — the IP address and
              browser used when you sign in or when an administrative action is taken, kept as part
              of the audit trail.
            </li>
          </ul>
        </Clause>

        <Clause number="3" title="Information about other people">
          <p>
            When you name guarantors on a loan application you give us their name, relationship to
            you, telephone numbers, address and occupation. We use those details for one purpose: to
            telephone them and confirm they are willing to stand for you.
          </p>
          <p>
            Please tell your guarantors that you are naming them and that we will call. You are
            responsible for having their agreement before you submit their details. The same applies
            to your next of kin.
          </p>
        </Clause>

        <Clause number="4" title="Why we hold it">
          <ul className="ml-5 list-disc space-y-1.5">
            <li>To confirm you are a member entitled to borrow.</li>
            <li>To verify your identity, as any responsible lender must.</li>
            <li>To assess and decide your loan applications.</li>
            <li>To pay approved loans into your bank account.</li>
            <li>
              To contact you about your loans — reminders before a repayment is due, confirmations
              when one is received, and a call if you fall behind.
            </li>
            <li>To keep an accurate record of every decision taken about your file.</li>
          </ul>
        </Clause>

        <Clause number="5" title="How it is protected">
          <ul className="ml-5 list-disc space-y-1.5">
            <li>
              Your identification number is <strong className="text-foreground">encrypted</strong>{' '}
              in the database. Administrators see it masked; the full number is only visible by
              opening the document you uploaded, and that action is itself recorded.
            </li>
            <li>
              Your password is stored as an{' '}
              <strong className="text-foreground">Argon2id hash</strong>, never as text. Nobody at
              UNIFEMGA can read it or tell you what it is.
            </li>
            <li>
              Staff access is limited by role. A loan officer does not automatically have access to
              your identity file.
            </li>
            <li>
              Every administrative action is written to an{' '}
              <strong className="text-foreground">append-only audit trail</strong> in the same
              transaction as the change it describes. There is no endpoint in the system that can
              edit or delete an audit entry.
            </li>
          </ul>
        </Clause>

        <Clause number="6" title="Who else sees it">
          <p>
            We do not sell your information and we do not share it for marketing. It is disclosed
            only:
          </p>
          <ul className="ml-5 list-disc space-y-1.5">
            <li>
              to UNIFEMGA officers who need it to administer your membership and your loans;
            </li>
            <li>
              to the service providers that deliver our email and SMS messages, which necessarily
              receive your email address or telephone number;
            </li>
            <li>
              to your guarantors, to the limited extent of telling them which loan they are being
              asked to stand for; and
            </li>
            <li>where we are required to by law.</li>
          </ul>
        </Clause>

        <Clause number="7" title="How long we keep it">
          <p>
            Loan and repayment records are kept for as long as the association needs them as a
            financial record, which is longer than the life of the loan itself. Audit entries are
            never deleted — that is what makes them an audit trail. If you close your membership,
            tell us and we will remove what we are not required to retain.
          </p>
        </Clause>

        <Clause number="8" title="Your rights">
          <p>
            You can see and correct most of what we hold from your profile in the portal. Once your
            KYC has been approved, your legal name, date of birth and identification are locked to
            protect the verification — contact us if any of them need to change.
          </p>
          <p>
            You may also ask us for a copy of your information, ask us to correct it, or object to a
            particular use of it. Write to{' '}
            <a href={`mailto:${org['org.email']}`} className="font-semibold text-primary hover:underline">
              {org['org.email']}
            </a>
            .
          </p>
        </Clause>

        <Clause number="9" title="Messages you cannot switch off">
          <p>
            Repayment reminders, receipts and arrears notices are part of holding a loan, not
            marketing, so there is no setting to disable them. Keeping your telephone number and
            email address current in your profile is how you make sure they reach you.
          </p>
        </Clause>

        <Clause number="10" title="Changes to this policy">
          <p>
            If we change this policy we will publish the new version here with a new version number.
            Where a change is significant we will tell members directly.
          </p>
        </Clause>

        <p className="border-t border-border pt-8 text-sm text-foreground-secondary">
          See also our{' '}
          <Link to="/terms" className="font-semibold text-primary hover:underline">
            terms and conditions
          </Link>
          .
        </p>
      </Prose>
    </>
  );
}

export function DraftNotice() {
  return (
    <div className="flex gap-3.5 rounded-2xl border border-warning/30 bg-warning-soft p-5">
      <AlertTriangle size={19} className="mt-0.5 shrink-0 text-warning" />
      <div className="text-sm leading-relaxed text-warning-soft-foreground">
        <strong className="font-bold">Draft pending adoption.</strong> This document describes how
        the platform actually behaves, but it has not yet been reviewed or adopted by the
        Foundation or the UNIFEMGA executive, and it is not legal advice. Treat it as a working draft until the association
        confirms it.
      </div>
    </div>
  );
}
